import { useState } from "react";
import { HelpCircle, ChevronDown, ChevronUp, Sparkles, QrCode, PhoneCall } from "lucide-react";
import { Link } from "react-router-dom";

export default function FaqPage() {
  const [openIdx, setOpenIdx] = useState(0);

  const faqs = [
    {
      q: "Do customers need to download an app to view the menu?",
      a: "No! Diners simply point their smartphone camera at the table QR code card. The digital menu opens instantly in their mobile web browser without installing any app.",
    },
    {
      q: "How fast can I update my menu prices or sold-out items?",
      a: "Instant! When you update prices, add new seasonal items, or toggle sold-out dishes in your Boss Panel, the changes reflect on all table QR scans within seconds.",
    },
    {
      q: "Can I get printed physical QR cards for my cafe tables?",
      a: "Yes. Tablezy provides high-resolution, print-ready QR artwork with your cafe logo and table numbers, perfect for acrylic stands, wood blocks, or table stickers.",
    },
    {
      q: "How long does it take to set up Tablezy for my restaurant?",
      a: "Our team sets up your complete digital menu, categories, dish images, and custom QR codes within 24 to 48 hours after receiving your menu details.",
    },
    {
      q: "Can I highlight Veg, Non-Veg, or Chef's Special tags?",
      a: "Yes. Every menu item can feature green/red dietary tags, category labels, price tags, high-definition dish photos, and detailed descriptions.",
    },
    {
      q: "What if I have multiple branches or table layouts?",
      a: "Tablezy supports table-assigned QR codes for any number of tables (1 to 100+) and multiple restaurant locations under one simple management platform.",
    },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12 font-body">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-mono font-semibold">
          <HelpCircle className="w-3.5 h-3.5" /> Frequently Asked Questions
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900">
          Got Questions? We Have Answers.
        </h1>
        <p className="text-slate-600 text-base">
          Everything you need to know about Tablezy digital QR menus for cafes and restaurants.
        </p>
      </div>

      {/* Accordion List */}
      <div className="space-y-4">
        {faqs.map((faq, idx) => {
          const isOpen = openIdx === idx;
          return (
            <div
              key={idx}
              className="bg-card border border-border rounded-3xl overflow-hidden transition-all shadow-sm"
            >
              <button
                onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                className="w-full p-6 text-left flex items-center justify-between gap-4 font-heading font-bold text-lg text-slate-900 hover:text-primary transition-colors"
              >
                <span>{faq.q}</span>
                {isOpen ? (
                  <ChevronUp className="w-5 h-5 text-primary shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
                )}
              </button>

              {isOpen && (
                <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-border/60 pt-4 animate-fade-up">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Still Have Questions Box */}
      <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-10 text-center space-y-4 shadow-xl">
        <h3 className="font-heading text-2xl font-bold">Still Have Questions?</h3>
        <p className="text-slate-300 text-sm max-w-md mx-auto">
          Our restaurant tech specialists are ready to help you set up your QR menu.
        </p>
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            className="h-12 px-6 rounded-full bg-primary text-white text-sm font-semibold flex items-center gap-2 hover:bg-primary/90 transition-all"
          >
            <span>Request Demo & QR Menu</span>
          </Link>
          <Link
            to="/support"
            className="h-12 px-6 rounded-full border border-slate-700 hover:bg-slate-800 text-white text-sm font-medium flex items-center gap-2 transition-all"
          >
            <PhoneCall className="w-4 h-4 text-primary" />
            <span>Contact Support Desk</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
